import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Smartphone, Settings, Film, Share2, CheckCircle2, ArrowRight } from "lucide-react";

/**
 * HEVCHelpModal — explains why iPhone-recorded clips sometimes fail to load,
 * what HEVC vs H.264 means in plain language, and walks the coach through
 * the two fixes:
 *   1. Prevent it next time → iOS Settings → Camera → Formats → Most Compatible
 *   2. Fix this clip right now → iMovie → Share → Save Video
 *
 * Stylized iOS-mockup cards instead of real screenshots so the visuals
 * stay correct across iOS updates and look on-brand with PBCOACH.APP.
 */
export default function HEVCHelpModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#0a0a0a] border border-white/15 text-white max-h-[90vh] overflow-y-auto"
        data-testid="hevc-help-modal"
      >
        <DialogHeader>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold flex items-center gap-2">
            <Smartphone size={11} /> Codec Compatibility
          </div>
          <DialogTitle className="font-display text-3xl font-black uppercase tracking-tighter mt-1">
            Why this clip failed
          </DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-sm leading-relaxed pt-2">
            Your clip is recorded in <strong className="text-white">HEVC</strong> (also called H.265) — Apple's
            high-efficiency video codec. iPhones default to it for smaller file sizes, but browsers play it back inconsistently.
            We need <strong className="text-white">H.264</strong> (the older, universally-supported codec) for reliable analysis.
          </DialogDescription>
        </DialogHeader>

        {/* HEVC vs H.264 plain-language comparison */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="border border-red-500/30 bg-red-500/5 p-3">
            <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-1">HEVC / H.265</div>
            <div className="text-xs text-white/85 leading-relaxed">
              Smaller files, but ~30% of browsers can't decode it. Default iPhone format since iOS 11.
            </div>
          </div>
          <div className="border border-[#00ff88]/30 bg-[#00ff88]/5 p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#00ff88] font-bold mb-1">H.264 / MP4</div>
            <div className="text-xs text-white/85 leading-relaxed">
              Slightly larger files, but works in every browser on every device. The reliable choice.
            </div>
          </div>
        </div>

        {/* Fix 1: prevent next time */}
        <section className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 bg-[#38BDF8] text-black font-display font-black text-sm flex items-center justify-center">1</div>
            <h3 className="font-display text-lg font-black uppercase tracking-tight">Prevent it for next time</h3>
          </div>
          <p className="text-xs text-[#A3A3A3] mb-3 leading-relaxed pl-8">
            On your iPhone, tell the Camera app to record in H.264 by default. Takes 15 seconds.
          </p>
          <div className="pl-8 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <IosStep
              num="A"
              title="Open Settings"
              icon={<Settings size={20} className="text-white/85" />}
              caption="iOS Settings app"
            />
            <IosStep
              num="B"
              title="Camera → Formats"
              icon={<Smartphone size={20} className="text-white/85" />}
              caption="Scroll down to Camera, tap Formats"
            />
            <IosStep
              num="C"
              title='Tap "Most Compatible"'
              icon={<CheckCircle2 size={20} className="text-[#00ff88]" />}
              caption="Records in H.264 from now on"
              accent
            />
          </div>
          <div className="pl-8 mt-3 text-[11px] text-[#A3A3A3] italic">
            Tradeoff: H.264 files are ~30% larger than HEVC. For 1080p video at typical quality, that's roughly 1 MB extra per 10 seconds — negligible.
          </div>
        </section>

        {/* Fix 2: fix this clip right now */}
        <section className="mt-7 border-t border-white/10 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 bg-[#38BDF8] text-black font-display font-black text-sm flex items-center justify-center">2</div>
            <h3 className="font-display text-lg font-black uppercase tracking-tight">Fix this clip right now</h3>
          </div>
          <p className="text-xs text-[#A3A3A3] mb-3 leading-relaxed pl-8">
            Re-export the failed clip through iMovie — it transcodes to H.264 automatically. About 30 seconds for a 1-minute clip.
          </p>
          <div className="pl-8 space-y-2">
            <IosStepRow
              num="A"
              title="Open the clip in iMovie"
              caption='In Photos: tap the clip → Share → "Save Video" target → choose iMovie. Or open iMovie → New Project → import the clip.'
              icon={<Film size={16} className="text-white/85" />}
            />
            <IosStepRow
              num="B"
              title="Tap Done, then the Share icon"
              caption="Top-right square-with-arrow icon. iMovie will automatically transcode to H.264 on export."
              icon={<Share2 size={16} className="text-white/85" />}
            />
            <IosStepRow
              num="C"
              title='Choose "Save Video" and pick a resolution'
              caption="720p or 1080p both work fine. The saved file lands in Photos as MP4 — upload that one instead."
              icon={<CheckCircle2 size={16} className="text-[#00ff88]" />}
              accent
            />
          </div>
        </section>

        {/* Desktop fallback */}
        <section className="mt-6 border-t border-white/10 pt-5">
          <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-2">
            On a Mac or PC instead?
          </div>
          <p className="text-xs text-white/85 leading-relaxed">
            Drag the clip into <strong>HandBrake</strong> (free, handbrake.fr), pick the "Fast 1080p30" preset, click Start. Or open in <strong>QuickTime Player</strong> → File → Export As → 1080p. Both output H.264 MP4.
          </p>
        </section>

        <div className="mt-6 text-[11px] text-[#A3A3A3] italic border-l-2 border-[#38BDF8]/60 pl-3 leading-relaxed">
          We're working on auto-transcoding HEVC clips server-side so you won't need to think about this. Coming soon.
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Stylized iOS-mockup step card with a phone-screen-like frame.
function IosStep({ num, title, caption, icon, accent }) {
  return (
    <div className={`relative border ${accent ? "border-[#00ff88]/50 bg-[#00ff88]/5" : "border-white/15 bg-white/[0.02]"} p-3 flex flex-col items-center text-center gap-2`}>
      <div className="absolute -top-2 -left-2 w-5 h-5 bg-[#38BDF8] text-black font-display font-black text-[10px] flex items-center justify-center">
        {num}
      </div>
      <div className="mt-2">{icon}</div>
      <div className="text-[11px] font-display font-bold uppercase tracking-wide text-white">
        {title}
      </div>
      <div className="text-[10px] text-[#A3A3A3] leading-relaxed">
        {caption}
      </div>
    </div>
  );
}

function IosStepRow({ num, title, caption, icon, accent }) {
  return (
    <div className={`flex items-start gap-3 border ${accent ? "border-[#00ff88]/40 bg-[#00ff88]/5" : "border-white/10 bg-white/[0.02]"} p-3`}>
      <div className="w-6 h-6 bg-[#38BDF8] text-black font-display font-black text-xs flex items-center justify-center flex-shrink-0">
        {num}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-xs font-display font-bold uppercase tracking-wide text-white">
            {title}
          </div>
          {icon}
        </div>
        <div className="text-[11px] text-[#A3A3A3] leading-relaxed mt-0.5">
          {caption}
        </div>
      </div>
      <ArrowRight size={14} className="text-[#A3A3A3] mt-2 flex-shrink-0" />
    </div>
  );
}
